"use client";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { WeaponDetail } from "@/types/weapon";
import { AttachmentsList } from "./attachments-list";
import { WeaponDisplay } from "./weapon-display";
import { WeaponStats } from "./weapon-stats";

interface WeaponDetailPanelProps {
  weaponDetail: WeaponDetail;
  label?: string; // e.g., "PRIMARY WEAPON", "SECONDARY WEAPON", or just "WEAPON"
}

// Define slot-based positioning for different attachment types
// Layout: Oval arrangement starting from left and going clockwise
const getSlotPosition = (slotType: string, index: number) => {
  const slotPositions: Record<string, string[]> = {
    // Left side - starting position
    muzzle: ["top-1/2 left-4 -translate-y-1/2"],

    // Top left
    "right accessory": ["top-16 left-24"],

    // Top center
    "top accessory": ["top-8 left-1/3"],

    // Top right
    optic: ["top-16 right-1/3"],

    // Right side
    ergonomics: ["top-1/2 right-1/4 "],

    // Bottom right
    ammo: ["bottom-12 right-1/3"],

    // Bottom center
    magazine: ["bottom-8 left-1/2 -translate-x-1/2"],

    // Bottom left
    underbarrel: ["bottom-16 left-1/4"],

    // Back to left side (inner oval)
    barrel: ["top-1/2 left-24 -translate-y-1/2"],
  };

  const positions = slotPositions[slotType] || ["top-1/4 right-1/4"];
  return positions[index % positions.length] || positions[0];
};

export function WeaponDetailPanel({
  weaponDetail,
  label = "WEAPON",
}: WeaponDetailPanelProps) {
  const usedPoints = weaponDetail.attachments.reduce(
    (sum: number, attachment) => sum + attachment.cost,
    0
  );
  const totalPoints = weaponDetail.weapon.attachmentPoints;

  return (
    <div className="overflow-hidden rounded-[8px] border border-[#262A2D] bg-[#141618]">
      {/* Header */}
      <div className="flex items-center justify-between border-[#262A2D] border-b bg-gradient-to-r from-[#F5A23E]/[0.06] to-transparent p-4">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[#F5A23E] text-[11px] tracking-[0.2em]">
            {label}
          </span>
          <h2 className="font-bold font-heading text-[#EAEBEC] text-xl tracking-wide">
            {weaponDetail.weapon.name.toUpperCase()}
          </h2>
        </div>
        <div className="flex items-center gap-4">
          {/* Attachment Points Visualization */}
          <div className="flex items-center gap-3">
            {/* Points Grid Visualization */}
            <div className="flex max-w-52 flex-wrap gap-[3px]">
              {Array.from(
                { length: Math.ceil(totalPoints / 10) },
                (_, index) => {
                  const pointsInThisSquare = Math.min(
                    10,
                    totalPoints - index * 10
                  );
                  const usedInThisSquare = Math.max(
                    0,
                    Math.min(pointsInThisSquare, usedPoints - index * 10)
                  );
                  const opacity = usedInThisSquare / pointsInThisSquare;

                  return (
                    <div
                      className="relative h-3.5 w-3.5 rounded-sm bg-[#262A2D]"
                      // biome-ignore lint/suspicious/noArrayIndexKey: good
                      key={index}
                      title={`Square ${index + 1}: ${usedInThisSquare}/${pointsInThisSquare} points used`}
                    >
                      <div
                        className="h-full w-full rounded-sm bg-[#F5A23E] transition-all duration-500"
                        style={{
                          opacity,
                          transform: `scale(${Math.max(0.1, opacity)})`,
                        }}
                      />
                    </div>
                  );
                }
              )}
            </div>
            <div className="text-right">
              <div className="font-bold font-mono text-[#F5A23E] text-base">
                {usedPoints}/{totalPoints}
              </div>
              <div className="font-mono text-[#6a7075] text-[9px] tracking-[0.1em]">
                ATTACHMENT PTS
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Left Panel - Weapon Display */}
        <div className="relative w-full overflow-hidden border-[#262A2D] md:w-2/3 md:border-r">
          {/* Weapon Visualization Area */}
          <div
            className="group relative flex h-72 items-center justify-center bg-[#101214]"
            style={{
              backgroundImage:
                "linear-gradient(#1c1f22 1px, transparent 1px), linear-gradient(90deg, #1c1f22 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }}
          >
            {/* corner brackets */}
            <div className="absolute top-4 left-4 h-5 w-5 border-[#33383C] border-t-2 border-l-2" />
            <div className="absolute top-4 right-4 h-5 w-5 border-[#33383C] border-t-2 border-r-2" />
            <div className="absolute bottom-4 left-4 h-5 w-5 border-[#33383C] border-b-2 border-l-2" />
            <div className="absolute right-4 bottom-4 h-5 w-5 border-[#33383C] border-r-2 border-b-2" />
            <WeaponDisplay
              imageUrl={weaponDetail.weapon.imageUrl}
              name={weaponDetail.weapon.name}
            />

            {/* Enhanced Attachment Points Overlay */}
            <div className="absolute inset-0 z-10">
              {weaponDetail.attachments.map((attachment, index: number) => {
                const position = getSlotPosition(attachment.slotType, index);

                return (
                  <div
                    className={`absolute transform transition-all duration-500 hover:scale-110 ${position}`}
                    key={`attachment-point-${attachment._id}-${index}`}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="group relative">
                          {/* Glowing Ring Effect */}
                          <div className="-inset-2 absolute animate-pulse rounded-full bg-gradient-to-r from-[#F5A23E]/20 via-[#F5A23E]/40 to-[#F5A23E]/20 blur-sm" />

                          {/* Attachment Point Container */}
                          <div className="relative flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#F5A23E] bg-[#F5A23E]/10 shadow-[0_8px_32px_rgba(0,0,0,0.3)] backdrop-blur-md transition-all duration-300 hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)]">
                            {/* Attachment Icon/Logo */}
                            {attachment.logoUrl ? (
                              <div
                                aria-label={attachment.name}
                                className="h-5 w-5 bg-center bg-cover bg-no-repeat brightness-110"
                                role="img"
                                style={{
                                  backgroundImage: `url(${attachment.logoUrl})`,
                                }}
                              />
                            ) : (
                              <div className="flex h-5 w-5 items-center justify-center rounded bg-[#F5A23E]/40">
                                <div className="h-3 w-3 rounded-full bg-[#F5A23E]" />
                              </div>
                            )}

                            {/* Cost Badge */}
                            <div className="-bottom-1.5 -right-1.5 absolute flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#F5A23E]">
                              <span className="font-bold font-mono text-[#0a0b0c] text-[9px]">
                                {attachment.cost}
                              </span>
                            </div>
                          </div>

                          {/* Connection Line to Weapon */}
                          <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 opacity-30 transition-opacity duration-300 group-hover:opacity-60">
                            <div className="h-px w-8 bg-gradient-to-r from-transparent via-[#F5A23E] to-transparent" />
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        className="max-w-xs border border-[#F5A23E]/30 bg-[#0a0b0c]/90 backdrop-blur-sm"
                        side="bottom"
                        sideOffset={8}
                      >
                        <div className="space-y-1">
                          <div className="font-bold font-mono text-sm text-white">
                            {attachment.name.toUpperCase()}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-mono text-[#46C8E0]">
                              {attachment.slotType.toUpperCase()}
                            </span>
                            <span className="text-white/60">•</span>
                            <span className="font-bold font-mono text-[#F5A23E]">
                              {attachment.cost} PTS
                            </span>
                          </div>

                          {/* Stats Preview */}
                          <div className="flex flex-wrap gap-2 text-xs">
                            {Object.entries(attachment.stats)
                              .filter(([, value]) => value !== 0)
                              .slice(0, 3)
                              .map(([stat, value]) => {
                                const isPositive = value > 0;
                                return (
                                  <span
                                    className={`font-mono ${
                                      isPositive
                                        ? "text-green-400"
                                        : "text-red-400"
                                    }`}
                                    key={stat}
                                  >
                                    {isPositive ? "+" : ""}
                                    {value} {stat.toUpperCase()}
                                  </span>
                                );
                              })}
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Weapon Stats */}
          <div className="border-[#262A2D] border-t p-5">
            <div className="mb-4 font-mono text-[#46C8E0] text-[11px] tracking-[0.2em]">
              {"// PERFORMANCE"}
            </div>
            <WeaponStats
              attachmentStats={weaponDetail.attachments.map(
                (attachment) => attachment.stats
              )}
              baseStats={weaponDetail.weapon.baseStats}
            />
          </div>
        </div>

        {/* Right Panel - Attachments */}
        <AttachmentsList attachments={weaponDetail.attachments} />
      </div>
    </div>
  );
}
